import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import ProfileHero from './components/ProfileHero';
import EngagementMetricsCards from './components/EngagementMetricsCards';
import ActivityTimeline from './components/ActivityTimeline';
import CommentHistoryCard from './components/CommentHistoryCard';
import ReplyNotificationsPanel from './components/ReplyNotificationsPanel';
import CommentFilters from './components/CommentFilters';

const UserProfileActivityDashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [comments, setComments] = useState([]);
  const [filteredComments, setFilteredComments] = useState([]);
  const [replies, setReplies] = useState([]);
  const [articles, setArticles] = useState([]);
  const [metrics, setMetrics] = useState({ totalComments: 0, totalLikes: 0, topLikes: 0, totalReplies: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', articleId: '', sort: 'newest' });
  const [readReplies, setReadReplies] = useState(() => {
    try { return JSON.parse(localStorage.getItem('np_read_replies') || '[]'); } catch { return []; }
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchData = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      // Fetch user's comments with blog post info
      const { data: userComments } = await supabase?.from('comments')?.select('*, blog_posts(id, title, slug)')?.eq('author_email', user?.email)?.is('parent_id', null)?.order('created_at', { ascending: false });

      const commentsData = userComments || [];
      setComments(commentsData);

      // Fetch articles for filter dropdown
      const articleIds = [...new Set(commentsData.map(c => c.post_id).filter(Boolean))];
      if (articleIds?.length > 0) {
        const { data: articleData } = await supabase?.from('blog_posts')?.select('id, title')?.in('id', articleIds);
        setArticles(articleData || []);
      }

      // Fetch replies to user's comments
      if (commentsData?.length > 0) {
        const commentIds = commentsData?.map(c => c?.id);
        const { data: replyData } = await supabase?.from('comments')?.select('*, blog_posts(id, title)')?.in('parent_id', commentIds)?.order('created_at', { ascending: false });

        const repliesWithRead = (replyData || [])?.map(r => ({
          ...r,
          _read: readReplies?.includes(r?.id),
        }));
        setReplies(repliesWithRead);

        // Attach reply counts to comments
        const replyCountMap = {};
        (replyData || [])?.forEach(r => {
          replyCountMap[r.parent_id] = (replyCountMap?.[r?.parent_id] || 0) + 1;
        });
        const commentsWithReplies = commentsData?.map(c => ({ ...c, reply_count: replyCountMap?.[c?.id] || 0 }));
        setComments(commentsWithReplies);

        // Compute metrics
        const totalLikes = commentsData?.reduce((sum, c) => sum + (c?.likes_count || 0), 0);
        const topLikes = commentsData?.reduce((max, c) => Math.max(max, c?.likes_count || 0), 0);
        setMetrics({
          totalComments: commentsData?.length,
          totalLikes,
          topLikes,
          totalReplies: (replyData || [])?.length,
        });
      } else {
        setMetrics({ totalComments: 0, totalLikes: 0, topLikes: 0, totalReplies: 0 });
      }
    } catch (err) {
      console.error('Error fetching activity data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    if (isAuthenticated && user?.email) fetchData();
  }, [isAuthenticated, user?.email, fetchData]);

  // Apply filters
  useEffect(() => {
    let result = [...comments];

    if (filters?.dateFrom) {
      result = result?.filter(c => c?.created_at >= filters?.dateFrom);
    }
    if (filters?.dateTo) {
      const toDate = filters?.dateTo + 'T23:59:59';
      result = result?.filter(c => c?.created_at <= toDate);
    }
    if (filters?.articleId) {
      result = result?.filter(c => c?.post_id === filters?.articleId);
    }

    if (filters?.sort === 'oldest') {
      result?.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else if (filters?.sort === 'most_liked') {
      result?.sort((a, b) => (b?.likes_count || 0) - (a?.likes_count || 0));
    } else {
      result?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    setFilteredComments(result);
  }, [comments, filters]);

  const handleMarkAllRead = () => {
    const allIds = replies?.map(r => r?.id);
    const updated = [...new Set([...readReplies, ...allIds])];
    setReadReplies(updated);
    localStorage.setItem('np_read_replies', JSON.stringify(updated));
    setReplies(prev => prev?.map(r => ({ ...r, _read: true })));
  };

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0D1929', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', color: 'rgba(238,240,242,0.4)', letterSpacing: '0.2em' }}>LASTER...</div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#0D1929', color: '#EEF0F2' }}>
      {/* Header nav */}
      <nav style={{ background: 'rgba(13,25,41,0.95)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 24px', position: 'sticky', top: 0, zIndex: 100, backdropFilter: 'blur(8px)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '56px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: 'rgba(238,240,242,0.4)', letterSpacing: '0.1em' }}>
            <Link to="/" style={{ color: 'rgba(238,240,242,0.4)', textDecoration: 'none' }}>HJEM</Link>
            <span>/</span>
            <span style={{ color: '#3B6FD4' }}>MIN AKTIVITET</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Link to="/blog-news-landing-page-blogg-nyheter" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: 'rgba(238,240,242,0.5)', textDecoration: 'none', letterSpacing: '0.1em' }}>BLOGG</Link>
            <Link to="/nord-pass-subscription-portal" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: 'rgba(238,240,242,0.5)', textDecoration: 'none', letterSpacing: '0.1em' }}>MIN KONTO</Link>
            {/* Notification bell */}
            <div style={{ position: 'relative' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={replies?.filter(r => !r?._read)?.length > 0 ? '#E76F51' : 'rgba(238,240,242,0.4)'} strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              {replies?.filter(r => !r?._read)?.length > 0 && (
                <span style={{ position: 'absolute', top: '-4px', right: '-6px', background: '#E76F51', color: '#EEF0F2', fontFamily: 'Impact, sans-serif', fontSize: '0.55rem', width: '14px', height: '14px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {replies?.filter(r => !r?._read)?.length}
                </span>
              )}
            </div>
          </div>
        </div>
      </nav>
      {/* Profile Hero */}
      <ProfileHero user={user} metrics={metrics} loading={loading} />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 24px 80px' }}>
        {/* Engagement Metrics */}
        <EngagementMetricsCards metrics={metrics} loading={loading} />

        {/* Two-column layout: timeline + notifications */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '32px', marginBottom: '48px', alignItems: 'start' }}>
          <ActivityTimeline comments={comments} loading={loading} />
          <ReplyNotificationsPanel replies={replies} loading={loading} onMarkRead={handleMarkAllRead} />
        </div>

        {/* Comment History Section */}
        <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'baseline', gap: '16px' }}>
          <h2 style={{ fontFamily: 'Impact, sans-serif', fontSize: '1.8rem', color: '#EEF0F2', letterSpacing: '0.02em', transform: 'rotate(-0.5deg)', margin: 0 }}>KOMMENTARHISTORIKK</h2>
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', color: 'rgba(238,240,242,0.35)', letterSpacing: '0.1em' }}>
            {filteredComments?.length} / {comments?.length} KOMMENTARER
          </span>
        </div>

        {/* Filters */}
        <CommentFilters filters={filters} onFilterChange={setFilters} articles={articles} loading={loading} />

        {/* Comment cards grid */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {[1,2,3,4]?.map(i => (
              <div key={i} style={{ height: '180px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', animation: 'pulse 1.5s infinite' }} />
            ))}
          </div>
        ) : filteredComments?.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 24px', border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.01)' }}>
            <div style={{ fontFamily: 'Impact, sans-serif', fontSize: '3rem', color: 'rgba(59,111,212,0.15)', marginBottom: '16px' }}>INGEN KOMMENTARER</div>
            <p style={{ fontFamily: 'Georgia, serif', fontSize: '0.9rem', color: 'rgba(238,240,242,0.4)', fontStyle: 'italic', marginBottom: '24px' }}>
              {comments?.length === 0 ? 'Du har ikke postet noen kommentarer ennå.' : 'Ingen kommentarer matcher filteret ditt.'}
            </p>
            <Link
              to="/blog-news-landing-page-blogg-nyheter"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', color: '#3B6FD4', border: '1px solid #3B6FD444', padding: '10px 20px', textDecoration: 'none', letterSpacing: '0.1em' }}
            >
              → LES ARTIKLER OG KOMMENTER
            </Link>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {filteredComments?.map((comment, i) => (
              <CommentHistoryCard key={comment?.id} comment={comment} index={i} />
            ))}
          </div>
        )}

        {/* Recent activity feed */}
        {comments?.length > 0 && (
          <div style={{ marginTop: '64px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', marginBottom: '24px' }}>
              <h2 style={{ fontFamily: 'Impact, sans-serif', fontSize: '1.6rem', color: '#EEF0F2', letterSpacing: '0.02em', transform: 'rotate(0.3deg)', margin: 0 }}>SISTE AKTIVITET</h2>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)', marginLeft: '8px' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {[...comments]?.slice(0, 8)?.map((c, i) => (
                <div key={c?.id} style={{ display: 'flex', gap: '16px', padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', alignItems: 'flex-start' }}>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: 'rgba(238,240,242,0.25)', width: '24px', flexShrink: 0, paddingTop: '2px' }}>{String(i + 1)?.padStart(2, '0')}</div>
                  <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#3B6FD4', flexShrink: 0, marginTop: '6px' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: 'Georgia, serif', fontSize: '0.8rem', color: 'rgba(238,240,242,0.7)', fontStyle: 'italic', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c?.content?.slice(0, 80)}{c?.content?.length > 80 ? '…' : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: 'rgba(238,240,242,0.3)' }}>
                        {c?.blog_posts?.title?.slice(0, 40) || 'Ukjent artikkel'}
                      </span>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.55rem', color: '#E76F51' }}>♥ {c?.likes_count || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfileActivityDashboard;
